import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'
import { getTenantLogoUrl, getTenantId } from '@/utils/tenant'
import { authApi } from '@/api/auth'

export default function AccountSetupPage() {
  const navigate = useNavigate()
  const { user, currentMode } = useAuthStore()
  const { tenantInfo } = useTenantStore()
  const { t } = useTranslation('auth')
  const tenantId = getTenantId()
  const logoUrl = getTenantLogoUrl(tenantId, tenantInfo?.logoUrl)

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const primaryColor = tenantInfo?.primaryColor || undefined

  // Password validation
  const hasMinLength = newPassword.length >= 8
  const hasLetter = /[a-zA-Z]/.test(newPassword)
  const hasNumber = /[0-9]/.test(newPassword)
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0
  const isValid = hasMinLength && hasLetter && hasNumber && passwordsMatch

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return

    setError('')
    setIsLoading(true)

    try {
      await authApi.setupPassword(newPassword)
      // 설정 완료 → requiresSetup 해제
      useAuthStore.setState({ requiresSetup: false })
      // 역할에 따라 적절한 페이지로 이동
      navigate(currentMode === 'admin' ? '/admin' : '/my/courses')
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || t('accountSetup.changeFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={tenantInfo?.name || t('accountSetup.companyLogo')}
              className="mx-auto h-16 w-auto"
            />
          ) : (
            <div
              className="mx-auto w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: primaryColor || '#6366f1' }}
            >
              {tenantInfo?.name?.charAt(0) || 'Q'}
            </div>
          )}
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {t('accountSetup.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('accountSetup.description', { name: user?.fullName })}
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                {t('accountSetup.newPassword')}
              </label>
              <input
                id="newPassword"
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('accountSetup.newPasswordPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('accountSetup.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('accountSetup.confirmPasswordPlaceholder')}
              />
            </div>
          </div>

          {/* Validation indicators */}
          {newPassword.length > 0 && (
            <div className="space-y-1 text-sm">
              <div className={hasMinLength ? 'text-green-600' : 'text-gray-400'}>
                {hasMinLength ? '✓' : '✗'} {t('accountSetup.validation.minLength')}
              </div>
              <div className={hasLetter && hasNumber ? 'text-green-600' : 'text-gray-400'}>
                {hasLetter && hasNumber ? '✓' : '✗'} {t('accountSetup.validation.letterAndNumber')}
              </div>
              <div className={hasSpecial ? 'text-green-600' : 'text-gray-400'}>
                {hasSpecial ? '✓' : '✗'} {t('accountSetup.validation.specialChar')}
              </div>
              {confirmPassword.length > 0 && (
                <div className={passwordsMatch ? 'text-green-600' : 'text-red-500'}>
                  {passwordsMatch ? '✓' : '✗'} {t('accountSetup.validation.match')}
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={!isValid || isLoading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: primaryColor || undefined }}
          >
            {isLoading ? t('accountSetup.submitting') : t('accountSetup.submit')}
          </button>
        </form>
      </div>
    </div>
  )
}
